import { useState, type FormEvent } from 'react';
import { Button } from './Button.tsx';
import { Card } from './Card.tsx';
import { Field } from './Field.tsx';
import { MIN_PASSWORD, friendlyAuthError } from './SignIn.tsx';

export interface ResetPasswordProps {
  logoSrc?: string | undefined;
  /** checking: the link is being read; ready: signed in by the link; invalid: expired or opened elsewhere. */
  state: 'checking' | 'ready' | 'invalid';
  reason?: string | null | undefined;
  onUpdate: (password: string) => Promise<string | null>;
  onRequestLink: (email: string) => Promise<string | null>;
  continueHref: string;
  continueLabel: string;
}

/** Landing page for a password reset link: choose a new password, or ask for a fresh link. */
export function ResetPasswordScreen(p: ResetPasswordProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [sent, setSent] = useState(false);

  async function update(e: FormEvent) {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) return setError(`Use at least ${MIN_PASSWORD} characters.`);
    if (password !== confirm) return setError("The two passwords don't match.");
    setBusy(true);
    setError(null);
    const err = await p
      .onUpdate(password)
      .catch((x: unknown) => (x instanceof Error ? x.message : 'Something went wrong.'));
    setBusy(false);
    if (err) setError(friendlyAuthError(err));
    else setDone(true);
  }
  async function request(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const err = await p
      .onRequestLink(email)
      .catch((x: unknown) => (x instanceof Error ? x.message : 'Something went wrong.'));
    setBusy(false);
    if (err) setError(friendlyAuthError(err));
    else setSent(true);
  }

  let content;
  if (p.state === 'checking') content = <p style={{ margin: 0 }}>Checking your link…</p>;
  else if (done)
    content = (
      <div style={{ display: 'grid', gap: 12 }}>
        <strong style={{ fontSize: 18 }}>Password updated.</strong>
        <p style={{ margin: 0 }}>You're signed in. Use the new password next time.</p>
        <Button onClick={() => window.location.assign(p.continueHref)}>{p.continueLabel}</Button>
      </div>
    );
  else if (p.state === 'ready')
    content = (
      <form onSubmit={update} style={{ display: 'grid', gap: 12 }}>
        <Field
          label="New password"
          type={show ? 'text' : 'password'}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={MIN_PASSWORD}
          hint={`At least ${MIN_PASSWORD} characters.`}
        />
        <Field
          label="Type it again"
          type={show ? 'text' : 'password'}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          error={error ?? undefined}
        />
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={show}
            onChange={(e) => setShow(e.target.checked)}
            style={{ width: 18, height: 18 }}
          />
          Show password
        </label>
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save new password'}
        </Button>
      </form>
    );
  else if (sent)
    content = (
      <div style={{ display: 'grid', gap: 12 }}>
        <strong style={{ fontSize: 18 }}>Check your email</strong>
        <p style={{ margin: 0 }}>
          If there's an account for {email.trim()}, a new link is on its way. Open it on this
          device.
        </p>
      </div>
    );
  else
    content = (
      <form onSubmit={request} style={{ display: 'grid', gap: 12 }}>
        <p style={{ margin: 0 }}>
          This reset link has expired or was opened on a different device or browser.
          {p.reason ? ` (${p.reason})` : ''} Send yourself a new one.
        </p>
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          error={error ?? undefined}
        />
        <Button type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Email me a new link'}
        </Button>
        <a href={p.continueHref} style={{ color: 'var(--ink-soft)', fontSize: 14 }}>
          Back to sign in
        </a>
      </form>
    );

  return (
    <main style={{ maxWidth: 420, margin: '8vh auto', padding: 24, display: 'grid', gap: 16 }}>
      {p.logoSrc && (
        <img
          src={p.logoSrc}
          alt="Crave Pizza"
          style={{ height: 56, width: 'auto', justifySelf: 'start' }}
        />
      )}
      <h1 style={{ fontWeight: 800, margin: 0 }}>Set a new password</h1>
      <Card>{content}</Card>
    </main>
  );
}
